import { getAnimatedObjects, type AnimatedSheet, type TiledMap } from '@veriverse/map-format';
import { TILE_SIZE } from '@veriverse/shared';
import type Phaser from 'phaser';
import { spriteDepth } from './depth.js';

/**
 * Looping decorations from the map's `animations` layer (ADR-0040): a steaming cup, a fish tank,
 * a ticking clock. The sheets are plain files in `public/animated/`, so they stay out of the
 * first download, and only the ones a map uses are fetched. Each object is depth-sorted like a
 * sprite. With reduced motion every one holds its first frame. Copies of one sheet start on
 * different frames so a row of them does not move in step.
 */
export const animatedTextureKey = (sheet: string): string => `animated:${sheet}`;

export function loadAnimatedObjects(scene: Phaser.Scene, map: TiledMap): void {
  const base = import.meta.env.BASE_URL;
  for (const object of getAnimatedObjects(map)) {
    const key = animatedTextureKey(object.sheet);
    if (scene.textures.exists(key)) continue;
    scene.load.spritesheet(key, `${base}animated/${object.sheet}.png`, {
      frameWidth: object.spec.width * TILE_SIZE,
      frameHeight: object.spec.height * TILE_SIZE,
    });
  }
}

function ensureAnimation(scene: Phaser.Scene, sheet: string, spec: AnimatedSheet): string {
  const key = animatedTextureKey(sheet);
  if (!scene.anims.exists(key)) {
    scene.anims.create({
      key,
      frames: scene.anims.generateFrameNumbers(key, { start: 0, end: spec.frames - 1 }),
      frameRate: spec.fps,
      repeat: -1,
    });
  }
  return key;
}

export function addAnimatedObjects(
  scene: Phaser.Scene,
  map: TiledMap,
  reducedMotion: boolean,
): Phaser.GameObjects.Sprite[] {
  const sprites: Phaser.GameObjects.Sprite[] = [];
  for (const object of getAnimatedObjects(map)) {
    const key = animatedTextureKey(object.sheet);
    // A sheet that failed to load is left out, like a missing tileset image.
    if (!scene.textures.exists(key)) continue;
    const sprite = scene.add
      .sprite(object.x * TILE_SIZE, object.y * TILE_SIZE, key, 0)
      .setOrigin(0, 0)
      .setDepth(spriteDepth(object.sortRow));
    if (!reducedMotion) {
      sprite.play({
        key: ensureAnimation(scene, object.sheet, object.spec),
        startFrame: object.id % object.spec.frames,
      });
    }
    sprites.push(sprite);
  }
  return sprites;
}
